import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getProduct } from '../utils/catalog';
import type { CartItem, CartLine } from '../types/cart';
interface CartContextValue {
  lines: CartLine[];
  count: number;
  subtotal: number;
  isOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  addItem: (productId: string, quantity?: number, option?: string) => void;
  updateQuantity: (key: string, quantity: number) => void;
  removeItem: (key: string) => void;
  clearCart: () => void;
}
const STORAGE_KEY = 'ep-cart';
const CartContext = createContext<CartContextValue | null>(null);
function readStorage(): CartItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) as CartItem[] : [];
  } catch {
    return [];
  }
}
export function CartProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [items, setItems] = useState<CartItem[]>(readStorage);
  const [isOpen, setIsOpen] = useState(false);
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      /* stockage indisponible */
    }
  }, [items]);
  const lines = useMemo<CartLine[]>(() => items.flatMap(item => {
    const product = getProduct(item.productId);
    return product ? [{
      ...item,
      product
    }] : [];
  }), [items]);
  const addItem = useCallback((productId: string, quantity = 1, option?: string) => {
    const product = getProduct(productId);
    if (!product) return;
    const key = `${productId}::${option ?? ''}`;
    setItems(prev => {
      const existing = prev.find(i => i.key === key);
      if (existing) {
        return prev.map(i => i.key === key ? {
          ...i,
          quantity: Math.min(product.stock, i.quantity + quantity)
        } : i);
      }
      return [...prev, {
        key,
        productId,
        option,
        quantity: Math.min(product.stock, quantity)
      }];
    });
    setIsOpen(true);
  }, []);
  const updateQuantity = useCallback((key: string, quantity: number) => {
    setItems(prev => prev.flatMap(i => {
      if (i.key !== key) return [i];
      if (quantity <= 0) return [];
      const stock = getProduct(i.productId)?.stock ?? quantity;
      return [{
        ...i,
        quantity: Math.min(stock, quantity)
      }];
    }));
  }, []);
  const removeItem = useCallback((key: string) => {
    setItems(prev => prev.filter(i => i.key !== key));
  }, []);
  const clearCart = useCallback(() => setItems([]), []);
  const value = useMemo<CartContextValue>(() => ({
    lines,
    count: lines.reduce((sum, l) => sum + l.quantity, 0),
    subtotal: lines.reduce((sum, l) => sum + l.quantity * l.product.price, 0),
    isOpen,
    openCart: () => setIsOpen(true),
    closeCart: () => setIsOpen(false),
    addItem,
    updateQuantity,
    removeItem,
    clearCart
  }), [lines, isOpen, addItem, updateQuantity, removeItem, clearCart]);
  return <CartContext.Provider value={value} data-id="element-636" data-mp="2oca695oqi9po">{children}</CartContext.Provider>;
}
export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart doit être utilisé dans CartProvider');
  return ctx;
}